import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { useState } from "react";
import { GREEN, YELLOW, RED, BLUE } from "@helper/colors";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { Formik, Form } from "formik";
import { updateTreeThreshold } from "@modules/Trees/services/treeCrud";
import { useMutation, useQueryClient } from "react-query";

type UpdateTreeThresholdPayload = {
  id: number;
  formData: {
    yellow_threshold: number;
    green_threshold: number;
    blue_threshold: number;
  };
};

type ProjectCache = {
  areas?: {
    tree_set?: any[];
  }[];
};

type TreesPaginated = {
  results: any[];
};

type TreesCache = TreesPaginated | any[];

function HealthThresholdSlider({ tree, setEdit }) {
  const blueThreshold =
    tree?.blue_threshold && Number.isFinite(tree.blue_threshold) && tree.blue_threshold > 0
      ? tree.blue_threshold
      : 100;

  const [thresholds, setThresholds] = useState<[number, number]>([
    tree ? tree.yellow_threshold : 50,
    tree ? tree.green_threshold : 70,
  ]);

  const sliderMax = blueThreshold;

  const getPercent = (value: number) => (value / sliderMax) * 100;

  const marks = [
    { value: thresholds[0], label: `${thresholds[0]}%` },
    { value: thresholds[1], label: `${thresholds[1]}%` },
    { value: sliderMax, label: `${sliderMax}%` },
  ];

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isLoading, mutateAsync: mutateAsyncTree } = useMutation<
    any,
    Error,
    UpdateTreeThresholdPayload
  >("updateTreeThreshold", updateTreeThreshold, {
    onSuccess: () => {
      const projectQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === "project",
      });

      projectQueries.forEach(([queryKey, projectData]) => {
        if (projectData) {
          queryClient.setQueryData(queryKey, (oldProject: ProjectCache | undefined) => {
            if (!oldProject) return oldProject;

            return {
              ...oldProject,
              areas:
                oldProject.areas?.map((area) => ({
                  ...area,
                  tree_set:
                    area.tree_set?.map((treeItem) => {
                      if (treeItem.id === tree.id) {
                        return {
                          ...treeItem,
                          yellow_threshold: thresholds[0],
                          green_threshold: thresholds[1],
                          blue_threshold: blueThreshold,
                        };
                      }
                      return treeItem;
                    }) ?? [],
                })) ?? [],
            };
          });
        }
      });

      const treesQueries = queryClient.getQueriesData({
        predicate: (query) => query.queryKey[0] === "trees",
      });
      treesQueries.forEach(([queryKey, treesData]) => {
        if (treesData) {
          queryClient.setQueryData(queryKey, (oldTrees: TreesCache | undefined) => {
            if (!oldTrees) return oldTrees;

            if (Array.isArray(oldTrees)) {
              return oldTrees.map((treeItem) =>
                treeItem.id === tree.id
                  ? {
                      ...treeItem,
                      yellow_threshold: thresholds[0],
                      green_threshold: thresholds[1],
                      blue_threshold: blueThreshold,
                    }
                  : treeItem
              );
            }

            return {
              ...oldTrees,
              results: oldTrees.results.map((treeItem) =>
                treeItem.id === tree.id
                  ? {
                      ...treeItem,
                      yellow_threshold: thresholds[0],
                      green_threshold: thresholds[1],
                      blue_threshold: blueThreshold,
                    }
                  : treeItem
              ),
            };
          });
        }
      });

      queryClient.invalidateQueries(["tree", `${tree.id}`]);
      queryClient.invalidateQueries("trees");

      setEdit(false);
    },
  });

  const initialValues = {
    yellow_threshold: thresholds[0],
    green_threshold: thresholds[1],
    blue_threshold: blueThreshold,
  };

  const handleSubmit = async () => {
    const treeFormData = {
      yellow_threshold: thresholds[0],
      green_threshold: thresholds[1],
      blue_threshold: blueThreshold,
    };
    await mutateAsyncTree({ formData: treeFormData, id: tree.id });
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={() => {
        const errors = {};
        return errors;
      }}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
            }}
          >
            <Box
              sx={{
                position: "relative",
                height: "20px",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "0%",
                  width: `${getPercent(thresholds[0])}%`,
                  height: "100%",
                  backgroundColor: RED,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: `${getPercent(thresholds[0])}%`,
                  width: `${getPercent(thresholds[1] - thresholds[0])}%`,
                  height: "100%",
                  backgroundColor: YELLOW,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: `${getPercent(thresholds[1])}%`,
                  width: `${getPercent(sliderMax - thresholds[1])}%`,
                  height: "100%",
                  backgroundColor: GREEN,
                }}
              />
              <Slider
                aria-labelledby="track-false-range-slider"
                getAriaValueText={(value) => `${value}%`}
                value={thresholds}
                min={0}
                max={sliderMax}
                onChange={(_event: Event, newValue: number | number[]) => {
                  if (Array.isArray(newValue) && newValue.length === 2) {
                    const [yellow, green] = newValue;
                    if (yellow !== green && yellow >= 0 && green <= sliderMax) {
                      setThresholds([yellow, green]);
                    }
                  }
                }}
                marks={marks}
                step={1}
                disableSwap
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 7 }}>
              <Button
                style={{ justifyContent: "flex-start" }}
                onClick={() => setThresholds([50, Math.min(70, sliderMax)])}
              >
                {t("set_default")}
              </Button>
              <Button
                style={{
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  alignSelf: "flex-end",
                }}
                type="submit"
              >
                {isLoading ? t("button.updating") : t("button.update")}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default HealthThresholdSlider;
